import { useState } from "react";
import { motion } from "framer-motion";
import { Building2, GraduationCap, Check } from "lucide-react";

const MODULES = [
  { code: "01", title: "Trauma — The Root Issue", text: "Gang involvement, addiction, and crime are symptoms. The program starts where the behavior starts: the wound underneath." },
  { code: "02", title: "Mindset Reconstruction", text: "Success is a mindset — but so is recidivism. Residents learn to spot the thought patterns that put them back in a cell." },
  { code: "03", title: "Accountability & Ownership", text: "No excuses, no blame loops. Ownership of the past as the foundation for leading the future." },
  { code: "04", title: "Survival to Strategy", text: "The same instincts that kept someone alive on the street get re-aimed — planning, discipline, and long-game thinking." },
  { code: "05", title: "Re-Entry Leadership", text: "Residents leave able to lead themselves first — then their families, their peers, and their communities." },
];

const TRACKS = {
  adult: {
    label: "Adult Facilities",
    icon: Building2,
    points: [
      "Re-entry readiness and workforce mindset",
      "Peer mentorship structures inside the unit",
      "Family reunification and communication skills",
      "Financial literacy and decision-making fundamentals",
    ],
  },
  juvenile: {
    label: "Juvenile Facilities",
    icon: GraduationCap,
    points: [
      "Identity work beyond the label of 'offender'",
      "Trauma processing through guided journaling",
      "Education engagement and goal mapping",
      "Conflict resolution and impulse control",
    ],
  },
};

export default function ProgramCurriculum() {
  const [track, setTrack] = useState("adult");
  const ActiveIcon = TRACKS[track].icon;

  return (
    <section id="program" className="relative py-28 lg:py-36 bg-[#151821]/40 grain" data-testid="program-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="font-mono-tactical text-xs uppercase tracking-[0.25em] text-amber-500"
        >
          The S2S Leadership Program
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mt-5 font-display font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#F3F4F6] max-w-2xl"
          data-testid="program-heading"
        >
          Five modules that plant the seeds of a new mindset.
        </motion.h2>
        <motion.a
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
          href="/api/syllabus.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-7 inline-flex items-center gap-2 border border-amber-500/50 text-amber-500 hover:bg-amber-500 hover:text-[#0D0E11] font-semibold text-sm px-6 py-3 rounded-full transition-colors"
          data-testid="program-download-syllabus-btn"
        >
          ↓ Download Program One-Pager (PDF)
        </motion.a>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {MODULES.map((m, i) => (
            <motion.div
              key={m.code}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.08 }}
              className={`group bg-[#151821] border border-white/10 hover:border-amber-500/40 rounded-xl p-7 transition-colors ${i === 0 ? "md:col-span-2 lg:col-span-1" : ""}`}
              data-testid={`module-card-${m.code}`}
            >
              <div className="font-mono-tactical text-xs text-amber-500 tracking-[0.2em]">MODULE {m.code}</div>
              <h3 className="mt-3 font-display font-bold text-lg sm:text-xl text-[#F3F4F6] tracking-tight">{m.title}</h3>
              <p className="mt-3 text-sm text-[#9CA3AF] leading-relaxed">{m.text}</p>
            </motion.div>
          ))}

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="bg-[#0D0E11] border border-amber-500/30 rounded-xl p-7"
            data-testid="track-customization-card"
          >
            <div className="flex gap-2 mb-5">
              {Object.entries(TRACKS).map(([key, t]) => (
                <button
                  key={key}
                  onClick={() => setTrack(key)}
                  className={`px-4 py-1.5 rounded-full text-xs font-mono-tactical tracking-wider transition-colors ${
                    track === key ? "bg-amber-500 text-[#0D0E11] font-bold" : "border border-white/15 text-[#9CA3AF] hover:text-[#F3F4F6]"
                  }`}
                  data-testid={`track-tab-${key}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <ActiveIcon size={20} className="text-amber-500" />
              <h3 className="font-display font-bold text-lg text-[#F3F4F6]">{TRACKS[track].label} Track</h3>
            </div>
            <ul className="mt-4 space-y-3">
              {TRACKS[track].points.map((p, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-[#9CA3AF]">
                  <Check size={15} className="text-amber-500 mt-0.5 shrink-0" /> {p}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
